/**
 * Tiny pub/sub bridge so anywhere in the app can ask the Calendar page to open a
 * specific appointment: switch to day view on its date, scroll it into view, and
 * highlight it. Today the only publisher is the Messages participants panel
 * (double-click an appointment card).
 *
 * Pages are <Activity> panes whose effects unmount while hidden, so the target is
 * BUFFERED: App switches the page, and the Calendar pane drains the pending target
 * when its effects (re)mount. Same shape as notification-navigation.js.
 *
 * Keep this module dependency-free (no project imports) to avoid import cycles —
 * it is imported by the Messages panel, App, and the Calendar page.
 */

let pendingTarget = null;
const listeners = new Set();

function normalizeTarget({ appointmentId, startAt } = {}) {
  const id = String(appointmentId || '').trim();
  const start = String(startAt || '').trim();
  if (!id || !start) {
    return null;
  }
  return { appointmentId: id, startAt: start };
}

/**
 * Publish a request to reveal `appointmentId` on the calendar. `startAt` is the
 * appointment's ISO start — the calendar needs it to pick the day BEFORE its own
 * events for that day have loaded.
 */
export function requestOpenCalendarAppointment(target) {
  const next = normalizeTarget(target);
  if (!next) {
    return;
  }
  pendingTarget = next;
  for (const listener of listeners) {
    try {
      listener(next);
    } catch (_error) {
      // ignore listener failures
    }
  }
}

/**
 * Subscribe to open-appointment requests. Returns an unsubscribe function.
 */
export function subscribeOpenCalendarAppointment(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Return and clear the pending target, if any. Used by the Calendar pane to pick up
 * a request that fired while it was hidden (its subscription was unmounted).
 */
export function consumePendingOpenCalendarAppointment() {
  if (!pendingTarget) {
    return null;
  }
  const target = pendingTarget;
  pendingTarget = null;
  return target;
}
